<script setup>
import { useNotifier } from '@daysmart/daysmart-vue-components/composables'
import mod from '@/core/utils/url/mod'

const props = defineProps({
  eventId: {
    type: [String, Number],
    required: true,
  },
  availableMemberships: {
    type: Array,
    required: true,
  },
  isAppointment: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const emit = defineEmits(['back', 'update'])
const { showPriceInclusiveOfFees } = useSessionData()
const selectedCustomer = useSelectedCustomer()
const api = useApi()
const { t } = useI18n()
const { r } = useRoutes()
const notify = useNotifier()

function getTranslatedMembershipType(type) {
  switch (type) {
    case 'pack':
      return t('Pack')
    case 'recurring_plan':
      return t('Recurring Plan')
    case 'prepaid_plan':
      return t('Prepaid Plan')
    default:
      return ''
  }
}

async function purchaseMembership({ id, type, plan, finishProcessing }) {
  try {
    let purchaseMembershipData
    if (type) {
      purchaseMembershipData = await api.initiateMembershipPurchase(id, {
        data: {
          customer: selectedCustomer.value,
          return_to: mod({
            url: window.location.href,
            excludeParams: ['auth0IdToken'],
          }),
          ...(type === 'recurring_plan' && { payment_plan: plan.id }),
          ...(props.isAppointment
            ? { appointment_slot: props.eventId }
            : { event: props.eventId }),
        },
      })
    }
    else {
      if (props.isAppointment) {
        purchaseMembershipData = await api.initiateAppointmentSlotPurchase(
          props.eventId,
          {
            data: {
              customer: selectedCustomer.value,
              return_to: mod({
                url: window.location.href,
                excludeParams: ['auth0IdToken'],
              }),
            },
          },
        )
      }
      else {
        purchaseMembershipData = await api.initiatePurchase(props.eventId, {
          data: {
            customer: selectedCustomer.value,
            return_to: mod({
              url: window.location.href,
              excludeParams: ['auth0IdToken'],
            }),
            event: props.eventId,
          },
        })
      }
    }

    if (purchaseMembershipData.next_url) {
      window.location.href = r({
        path: purchaseMembershipData.next_url,
        isV2Path: false,
        applyCurrentProviderPrefix: false,
      })
    }
  }
  catch (error) {
    if (error.response && error.response.data && error.response.data.field_errors) {
      const fieldErrors = error.response?.data?.field_errors
      const nonFieldErrorMessage = Array.isArray(fieldErrors?.non_field_errors) && fieldErrors.non_field_errors.length > 0
        ? fieldErrors.non_field_errors[0]
        : null

      const hasEventFullError = Object.values(fieldErrors).some(errors =>
        errors.some(error => error.includes('event_full')),
      )

      if (hasEventFullError) {
        emit('update')
      }

      if (nonFieldErrorMessage) {
        notify.emit({
          type: 'danger',
          title: t('Error'),
          content: nonFieldErrorMessage,
        })
      }
    }
    finishProcessing()
  }
}

function getDisplayPrice(membership) {
  if (showPriceInclusiveOfFees) {
    return membership.display_price.string
  }
  return membership.price.string
}
</script>

<template>
  <DSLoadingWrapper :loading="!availableMemberships">
    <div class="border-1 border-surface-600 bg-surface-200 p-4">
      <DSButton
        class="text-xs text-secondary-300"
        @click="emit('back')"
      >
        <i class="i-fas-chevron-left" />
        {{ t('Back') }}
      </DSButton>
      <div
        v-for="membership in availableMemberships"
        :key="membership.name"
        class="my-2 border-1 border-surface-600 bg-white p-4"
      >
        <div class="flex flex-col flex-gap-y-0.5">
          <p
            v-if="membership?.name"
            class="text-text fw-600"
          >
            {{ membership?.name }}
          </p>
          <p
            v-if="membership.type"
            class="text-text capitalize"
          >
            {{ getTranslatedMembershipType(membership.type) }}
          </p>
        </div>
        <template v-if="membership.type === 'recurring_plan'">
          <template
            v-for="plan in membership.plans"
            :key="plan.id"
          >
            <div
              v-if="plan.status === 'for_sale'"
              class="flex flex-col flex-gap-y-0.5 m-block-4"
            >
              <p class="text-sm text-text-500">
                *{{ plan?.duration_description }}
              </p>
              <DSTooltip
                v-if="membership.purchasable_only_by_provider"
                :content="t('Membership is only available for purchase by the business.')"
              >
                <DSButton
                  class="text-text text btn-md w-100% text-xs fw-600 uppercase btn-outline-secondary"
                  :disabled="true"
                >
                  {{ plan.price_with_period }}
                </DSButton>
              </DSTooltip>
              <DSAsyncButton
                v-else
                class="text-text text btn-md w-100% text-xs fw-600 uppercase btn-outline-secondary"
                @submit="
                  finishProcessing =>
                    purchaseMembership({
                      id: membership.id,
                      type: membership.type,
                      plan,
                      finishProcessing,
                    })
                "
              >
                {{ plan.price_with_period }}
              </DSAsyncButton>
            </div>
          </template>
        </template>
        <template v-else>
          <div class="flex flex-col flex-gap-y-2">
            <p
              v-if="membership?.description"
              v-sanitize="membership?.description"
              class="text-sm text-text-500"
            />
            <DSAsyncButton
              v-if="membership?.price && !membership?.purchasable_only_by_provider"
              class="text-text btn-md w-100% text-xs fw-600 uppercase btn-outline-secondary"
              @submit="
                finishProcessing =>
                  purchaseMembership({
                    id: membership.id,
                    type: membership.type,
                    finishProcessing,
                  })
              "
            >
              {{ getDisplayPrice(membership) }}
            </DSAsyncButton>
            <DSTooltip
              v-else
              :content="t('Membership is only available for purchase by the business.')"
            >
              <DSButton
                class="text-text text btn-md w-100% text-xs fw-600 uppercase btn-outline-secondary"
                :disabled="true"
              >
                {{ getDisplayPrice(membership) }}
              </DSButton>
            </DSTooltip>
          </div>
        </template>
      </div>
    </div>
  </DSLoadingWrapper>
</template>

<i18n>
{
  "en-us": {
    "Single Session": "Single Session",
    "Pack": "Pack",
    "Recurring Plan": "Recurring Plan",
    "Prepaid Plan": "Prepaid Plan",
    "Back": "Back",
    "Pay for this single registration.": "Pay for this single registration.",
    "Membership is only available for purchase by the business.": "Membership is only available for purchase by the business.",
    "Error": "Error"
  }
}
</i18n>

<i18n locale="de" lang="json">
{
  "Pack": "Paket",
  "Recurring Plan": "Wiederkehrendes Abonnement",
  "Prepaid Plan": "Vorausbezahltes Abo",
  "Back": "Zurück",
  "Single Session": "Einzelsitzung",
  "Pay for this single registration.": "Für diese Einzelregistrierung bezahlen",
  "Membership is only available for purchase by the business.": "Die Mitgliedschaft kann nur vom Unternehmen gekauft werden."
}
</i18n>

<i18n locale="fr" lang="json">
{
  "Pack": "Pack",
  "Recurring Plan": "Forfait récurrent",
  "Prepaid Plan": "Forfait prépayé",
  "Back": "Retour",
  "Single Session": "Séance unique",
  "Pay for this single registration.": "Payer pour cette inscription simple.",
  "Membership is only available for purchase by the business.": "L'abonnement ne peut être acheté que par l'entreprise."
}
</i18n>

<i18n locale="es" lang="json">
{
  "Pack": "Paquete",
  "Recurring Plan": "Plan recurrente",
  "Prepaid Plan": "Plan de prepago",
  "Back": "Volver",
  "Single Session": "Sesión única",
  "Pay for this single registration.": "Pagar por esta inscripción.",
  "Membership is only available for purchase by the business.": "Solo las empresas pueden darse de alta para ser miembros."
}
</i18n>
